import { canManageOrganization } from 'util/organizationAccess';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faCheck,
  faEye,
  faMapMarkerAlt,
  faSort,
  faSortDown,
  faSortUp,
  faTimes,
  faUnlink
} from '@fortawesome/free-solid-svg-icons';
import CardDropdown from 'components/common/CardDropdown';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import SimpleBarReact from 'simplebar-react';
import { toast } from 'react-toastify';
import _ from 'lodash';
import moment from 'moment';
import { showAlert } from 'stores/modal';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useApproveShareRequestMutation,
  useAssignUserToLocationMutation,
  useCancelShareRequestMutation,
  useListShareRequestsQuery,
  useRejectShareRequestMutation,
  useRemoveAssociationMutation
} from 'rtk/org-admin/share-request.slice';
import { useListTenantLocationsQuery } from 'rtk/org-admin/location.slice';
const getRowUserGuid = (row, direction) => {
  // For SENT rows the target is the "To" user; for RECEIVED it's the "From" user
  return direction === 'RECEIVED'
    ? row?.FromUserMasterGuid || row?.FromUserGuid || row?.FromUserGUID
    : row?.ToUserMasterGuid || row?.ToUserGuid || row?.ToUserGUID;
};

const AdminNotariesShareTab = ({ direction, statusOptions }) => {
  const canManage = useSelector(state =>
    canManageOrganization(state.authentication?.userRoles)
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userMaster = useSelector(state => state.app.userMaster);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortOrder, setSortOrder] = useState('CreatedDescending');
  const [hasUserSortedConnectedSince, setHasUserSortedConnectedSince] =
    useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveId, setApproveId] = useState(null);

  const tenantGuid =
    userMaster?.TenantGUID ||
    userMaster?.TenantGuid ||
    userMaster?.tenantGuid ||
    userMaster?.TenantId ||
    userMaster?.TenantID ||
    userMaster?.tenant_id ||
    null;

  const { data: locationsRes } = useListTenantLocationsQuery(
    {
      tenantGuid,
      indexStart: 0,
      maxResults: 500
    },
    { skip: !tenantGuid }
  );

  const locationsByGuid = useMemo(() => {
    const objects = Array.isArray(locationsRes?.Objects)
      ? locationsRes.Objects
      : [];
    const map = {};
    objects.forEach(l => {
      if (l?.GUID) map[l.GUID] = l?.DisplayName || l.GUID;
    });
    return map;
  }, [locationsRes]);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectId, setRejectId] = useState(null);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelId, setCancelId] = useState(null);
  const [cancelName, setCancelName] = useState(null);

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeId, setRemoveId] = useState(null);
  const [removeName, setRemoveName] = useState(null);

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationUserGuid, setLocationUserGuid] = useState(null);
  const [locationDisplayName, setLocationDisplayName] = useState(null);
  const [selectedLocationGuid, setSelectedLocationGuid] = useState('');
  const [assigningLocation, setAssigningLocation] = useState(false);

  const indexStart = pageIndex * pageSize;
  const {
    data: shareRequestsRes,
    isFetching,
    isLoading,
    isError: shareRequestsLoadError,
    error: shareRequestsError,
    refetch: refetchShareRequests
  } = useListShareRequestsQuery({
    indexStart,
    maxResults: pageSize,
    sortOrder,
    direction,
    connectionsOnly: true,
    status: statusFilter
  });
  const loading = isFetching || isLoading;

  const [approveShareRequest] = useApproveShareRequestMutation();
  const [rejectShareRequest] = useRejectShareRequestMutation();
  const [cancelShareRequest] = useCancelShareRequestMutation();
  const [removeAssociation] = useRemoveAssociationMutation();
  const [assignUserToLocation] = useAssignUserToLocationMutation();

  const shareRequests = shareRequestsRes?.Objects || [];
  const totalRecords = shareRequestsRes?.TotalRecords ?? 0;
  const endOfResults = !!shareRequestsRes?.EndOfResults;
  const error = shareRequestsLoadError
    ? _.get(shareRequestsError, 'Message', 'Failed to load share requests')
    : null;

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const handleStatusFilter = value => {
    setStatusFilter(value);
    setPageIndex(0);
  };

  const handleView = row => {
    const guid = getRowUserGuid(row, direction);
    if (!guid) {
      toast.error('Unable to determine the notary GUID to view journals.');
      return;
    }
    navigate(`/admin/notaries/${guid}/entries`);
  };

  const openApproveModal = id => {
    if (!id || actioningId) return;
    setApproveId(id);
    setShowApproveModal(true);
  };
  const closeApproveModal = () => {
    if (actioningId) return;
    setShowApproveModal(false);
    setApproveId(null);
  };
  const handleApprove = () => {
    if (!approveId) return;
    setActioningId(approveId);
    approveShareRequest(approveId)
      .unwrap()
      .then(() => {
        toast.success('Request approved.');
        closeApproveModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Approve',
            err?.Message || 'Failed to approve this request.'
          )
        );
      })
      .finally(() => setActioningId(null));
  };

  const openRejectModal = id => {
    if (!id || actioningId) return;
    setRejectId(id);
    setShowRejectModal(true);
  };
  const closeRejectModal = () => {
    if (actioningId) return;
    setShowRejectModal(false);
    setRejectId(null);
  };
  const handleReject = () => {
    if (!rejectId) return;
    setActioningId(rejectId);
    rejectShareRequest(rejectId)
      .unwrap()
      .then(() => {
        toast.success('Request rejected.');
        closeRejectModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Reject',
            err?.Message || 'Failed to reject this request.'
          )
        );
      })
      .finally(() => setActioningId(null));
  };

  const openCancelModal = (id, name) => {
    if (!id || actioningId) return;
    setCancelId(id);
    setCancelName(name || null);
    setShowCancelModal(true);
  };
  const closeCancelModal = () => {
    if (actioningId) return;
    setShowCancelModal(false);
    setCancelId(null);
    setCancelName(null);
  };
  const handleCancel = () => {
    if (!cancelId) return;
    setActioningId(cancelId);
    cancelShareRequest(cancelId)
      .unwrap()
      .then(() => {
        toast.success('Request cancelled.');
        closeCancelModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Cancel',
            err?.Message || 'Failed to cancel request.'
          )
        );
      })
      .finally(() => setActioningId(null));
  };

  const openRemoveModal = (id, name) => {
    if (!id || actioningId) return;
    setRemoveId(id);
    setRemoveName(name || null);
    setShowRemoveModal(true);
  };
  const closeRemoveModal = () => {
    if (actioningId) return;
    setShowRemoveModal(false);
    setRemoveId(null);
    setRemoveName(null);
  };
  const handleRemoveConfirmed = () => {
    if (!removeId) return;
    setActioningId(removeId);
    removeAssociation(removeId)
      .unwrap()
      .then(() => {
        toast.success('Notary removed.');
        closeRemoveModal();
      })
      .catch(err => {
        dispatch(
          showAlert('Unable to Remove', err?.Message || 'Failed to remove.')
        );
      })
      .finally(() => setActioningId(null));
  };

  const handleRemove = row => {
    const id = row?.GUID || row?.Id || row?.id;
    const fromName = row?.FromUserName || row?.RequesterName || '-';
    const toName = row?.ToUserName || row?.Name || '-';
    const name = direction === 'RECEIVED' ? fromName : toName;
    const status = String(row?.status ?? row?.Status ?? '').toUpperCase();
    const isPending = status === 'PENDING' || status === 'REQUESTED';
    const isAccepted = status === 'ACCEPTED';
    if (!isPending && !isAccepted) return;
    if (!id) return;

    if (isPending) {
      openCancelModal(id, name);
    } else {
      openRemoveModal(id, name);
    }
  };

  const handleToggleConnectedSinceSort = () => {
    setHasUserSortedConnectedSince(true);
    setSortOrder(current =>
      current === 'CreatedDescending' ? 'CreatedAscending' : 'CreatedDescending'
    );
    setPageIndex(0);
  };

  const showBadgeColor = journalAccess => {
    if (journalAccess === 'N/A') return 'danger';
    if (journalAccess === 'Limited') return 'warning';
    return 'success';
  };
  const showBadgeLabel = journalAccess => {
    return journalAccess;
  };

  const toMomentFromNow = value => {
    if (!value) return '—';
    const m = moment(value);
    return m.isValid() ? m.fromNow(true) : value;
  };

  const nameLabel = direction === 'RECEIVED' ? 'From' : 'To';
  const emailLabel = direction === 'RECEIVED' ? 'From email' : 'To email';
  const dropdownId = `admin-notaries-${direction.toLowerCase()}-status-filter`;

  const openLocationModal = row => {
    if (!row) return;
    const userGuid = getRowUserGuid(row, direction);
    if (!userGuid) {
      toast.error('Unable to determine user GUID for location assignment.');
      return;
    }
    const fromName = row.FromUserName || row.RequesterName || '-';
    const toName = row.ToUserName || row.Name || '-';
    const name = direction === 'RECEIVED' ? fromName : toName;
    setLocationUserGuid(userGuid);
    setLocationDisplayName(name);
    setSelectedLocationGuid(row?.LocationGuid || '');
    setShowLocationModal(true);
  };

  const closeLocationModal = () => {
    if (assigningLocation) return;
    setShowLocationModal(false);
    setLocationUserGuid(null);
    setLocationDisplayName(null);
    setSelectedLocationGuid('');
  };

  const handleAssignLocation = () => {
    if (!tenantGuid) {
      toast.error('Tenant GUID not available.');
      return;
    }
    if (!locationUserGuid) {
      toast.error('User GUID not available.');
      return;
    }
    if (!selectedLocationGuid) {
      toast.warning('Please select a location.');
      return;
    }
    setAssigningLocation(true);
    assignUserToLocation({
      tenantGuid,
      locationGuid: selectedLocationGuid,
      userGuid: locationUserGuid
    })
      .unwrap()
      .then(() => {
        toast.success('Location assigned.');
        closeLocationModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Assign Location',
            err?.Message || 'Failed to assign location.'
          )
        );
      })
      .finally(() => setAssigningLocation(false));
  };

  return (
    <>
      <Modal
        show={showApproveModal}
        onHide={() => !actioningId && closeApproveModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Approve Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to approve this request?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeApproveModal}
            disabled={!!actioningId}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleApprove}
            disabled={!!actioningId}
          >
            {actioningId ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Approve
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRejectModal}
        onHide={() => !actioningId && closeRejectModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to reject this request?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeRejectModal}
            disabled={!!actioningId}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!!actioningId}
          >
            {actioningId ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Reject
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCancelModal}
        onHide={() => !actioningId && closeCancelModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            This will cancel the connection request for{' '}
            {cancelName || 'this notary'}.
            <br />
            You can send a new request later if needed.
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCancelModal}
            disabled={!!actioningId}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleCancel}
            disabled={!!actioningId}
          >
            {actioningId ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Cancel Request
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRemoveModal}
        onHide={() => !actioningId && closeRemoveModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove Associated Notary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            This will remove {removeName || 'this notary'} from your associated
            notaries.
            <br />
            They will no longer have access.
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeRemoveModal}
            disabled={!!actioningId}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRemoveConfirmed}
            disabled={!!actioningId}
          >
            {actioningId ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showLocationModal} onHide={closeLocationModal} centered>
        <Modal.Header closeButton={!assigningLocation}>
          <Modal.Title>Assign Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Assign a location for {locationDisplayName || 'this user'}.
          </p>
          <Form.Group className="mb-0">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={selectedLocationGuid || ''}
              onChange={e => setSelectedLocationGuid(e.target.value || '')}
              disabled={assigningLocation}
            >
              <option value="">Select location</option>
              {Object.entries(locationsByGuid)
                .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
                .map(([guid, label]) => (
                  <option key={guid} value={guid}>
                    {label}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeLocationModal}
            disabled={assigningLocation}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignLocation}
            disabled={assigningLocation}
          >
            {assigningLocation ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      {error && !loading && (
        <div className="alert alert-warning mb-3">
          {error}
          <Button
            variant="link"
            size="sm"
            className="p-0 ms-2"
            onClick={() => refetchShareRequests()}
          >
            Retry
          </Button>
        </div>
      )}

      {!error && (
        <Dropdown align="end" className="filter-dropdown mb-1">
          <Dropdown.Toggle
            variant="outline-primary"
            size="sm"
            id={dropdownId}
            style={{ minWidth: '200px' }}
          >
            {statusFilter === 'ALL' ? 'Status: All' : `Status: ${statusFilter}`}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="filter-dropdown-menu"
            style={{ minWidth: '200px' }}
          >
            {statusOptions
              .filter(s => s !== 'ALL')
              .map(option => (
                <Dropdown.Item
                  key={option}
                  className={
                    statusFilter === option
                      ? 'text-primary fw-semi-bold active'
                      : ''
                  }
                  onClick={() => handleStatusFilter(option)}
                >
                  {option}
                </Dropdown.Item>
              ))}
            <Dropdown.Divider />
            <Dropdown.Item
              eventKey="clear"
              onClick={() => handleStatusFilter('ALL')}
              className={
                statusFilter === 'ALL' ? 'text-primary fw-semi-bold' : ''
              }
            >
              Show All
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      )}

      {loading && !shareRequests?.length && (
        <Flex justifyContent="center" className="py-3">
          <Spinner size="sm" />
        </Flex>
      )}

      {!error && (
        <>
          {(shareRequests.length > 0 || pageIndex > 0 || totalRecords > 0) &&
            !isLoading && (
              <Pagination
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalRecords={totalRecords}
                currentCount={shareRequests.length}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                endOfResults={endOfResults}
                disabled={loading}
                onRefresh={() => refetchShareRequests()}
                loading={loading}
              />
            )}
          {shareRequests.length !== 0 && !isLoading && (
            <SimpleBarReact>
              <Table
                responsive
                striped
                bordered
                className={`fs--1 mb-0 overflow-hidden${
                  loading ? ' refreshing-skeleton' : ''
                }`}
              >
                <thead className="bg-200 text-900 text-nowrap align-middle">
                  <tr>
                    <th className="border-0 fw-semi-bold">{nameLabel}</th>
                    <th className="border-0 fw-semi-bold">{emailLabel}</th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Location
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Journal access
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Status
                    </th>
                    <th
                      className="border-0 fw-semi-bold d-none d-lg-table-cell cursor-pointer"
                      onClick={handleToggleConnectedSinceSort}
                    >
                      Requested on
                      <FontAwesomeIcon
                        icon={
                          !hasUserSortedConnectedSince
                            ? faSort
                            : sortOrder === 'CreatedDescending'
                            ? faSortDown
                            : faSortUp
                        }
                        className="ms-1 text-500"
                      />
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Expires on
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Connected since
                    </th>
                    <th className="border-0 fw-semi-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shareRequests.map(row => {
                    const id = row.GUID || row.Id || row.id;
                    const fromName =
                      row.FromUserName || row.RequesterName || '-';
                    const toName = row.ToUserName || row.Name || '-';
                    const name = direction === 'RECEIVED' ? fromName : toName;
                    const email =
                      direction === 'RECEIVED'
                        ? row.FromUserEmail || row.RequesterEmail || ''
                        : row.ToUserEmail || '';
                    const status = String(
                      row.status ?? row.Status ?? ''
                    ).toUpperCase();

                    const requestedOn = row.ReceivedDateTime ?? '—';
                    const expiresOn = row.ExpiresDateTime ?? '—';
                    const connectedSince = row.RespondedDateTime ?? '—';
                    return (
                      <tr key={id} className="align-middle white-space-nowrap">
                        <td className="align-middle py-2">{name}</td>
                        <td className="align-middle py-2">{email}</td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {row?.Location || '—'}
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          <SoftBadge
                            bg={showBadgeColor(row.JournalAccessRequested)}
                            pill
                          >
                            {showBadgeLabel(row.JournalAccessRequested)}
                          </SoftBadge>
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          <SoftBadge
                            bg={
                              status === 'PENDING'
                                ? 'warning'
                                : status === 'ACCEPTED'
                                ? 'success'
                                : 'secondary'
                            }
                            pill
                          >
                            {status || '—'}
                          </SoftBadge>
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {moment(requestedOn)
                            .local()
                            .format('MM/DD/YYYY h:mm A')}
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {moment(expiresOn)
                            .local()
                            .format('MM/DD/YYYY h:mm A')}
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {toMomentFromNow(connectedSince)}
                        </td>
                        <td className="align-middle py-2 text-end">
                          {(() => {
                            const st = status;
                            const isPending =
                              st === 'PENDING' || st === 'REQUESTED';
                            const isAccepted = st === 'ACCEPTED';
                            const shouldShowCancel =
                              canManage && direction === 'SENT' && isPending;
                            const shouldShowDisconnect =
                              canManage && isAccepted;
                            const shouldShowApproveReject =
                              canManage &&
                              direction === 'RECEIVED' &&
                              isPending;
                            const hasActions =
                              isAccepted ||
                              shouldShowApproveReject ||
                              shouldShowCancel ||
                              shouldShowDisconnect;

                            if (!hasActions) return null;

                            return (
                              <CardDropdown drop="start">
                                <div className="py-2">
                                  {isAccepted && (
                                    <>
                                      <Dropdown.Item
                                        href="#!"
                                        disabled={actioningId === id}
                                        onClick={() => handleView(row)}
                                      >
                                        <FontAwesomeIcon
                                          icon={faEye}
                                          className="me-2"
                                          fixedWidth
                                        />
                                        View
                                      </Dropdown.Item>
                                      {canManage && (
                                        <Dropdown.Item
                                          href="#!"
                                          disabled={actioningId === id}
                                          onClick={() => openLocationModal(row)}
                                        >
                                          <FontAwesomeIcon
                                            icon={faMapMarkerAlt}
                                            className="me-2"
                                            fixedWidth
                                          />
                                          Location
                                        </Dropdown.Item>
                                      )}
                                    </>
                                  )}
                                  {shouldShowApproveReject && (
                                    <>
                                      <Dropdown.Item
                                        href="#!"
                                        disabled={actioningId === id}
                                        onClick={() => openApproveModal(id)}
                                      >
                                        {actioningId === id ? (
                                          <>
                                            <Spinner
                                              animation="border"
                                              size="sm"
                                              className="me-1"
                                            />
                                            Approving…
                                          </>
                                        ) : (
                                          <>
                                            <FontAwesomeIcon
                                              icon={faCheck}
                                              className="me-2"
                                              fixedWidth
                                            />
                                            Approve
                                          </>
                                        )}
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        href="#!"
                                        className="text-danger"
                                        disabled={actioningId === id}
                                        onClick={() => openRejectModal(id)}
                                      >
                                        <FontAwesomeIcon
                                          icon={faTimes}
                                          className="me-2"
                                          fixedWidth
                                        />
                                        Reject
                                      </Dropdown.Item>
                                    </>
                                  )}
                                  {shouldShowCancel && (
                                    <Dropdown.Item
                                      href="#!"
                                      className="text-danger"
                                      disabled={actioningId === id}
                                      onClick={() => handleRemove(row)}
                                    >
                                      {actioningId === id ? (
                                        <>
                                          <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-1"
                                          />
                                          Cancelling…
                                        </>
                                      ) : (
                                        <>
                                          <FontAwesomeIcon
                                            icon={faBan}
                                            className="me-2"
                                            fixedWidth
                                          />
                                          Cancel
                                        </>
                                      )}
                                    </Dropdown.Item>
                                  )}
                                  {shouldShowDisconnect && (
                                    <Dropdown.Item
                                      href="#!"
                                      className="text-danger"
                                      disabled={actioningId === id}
                                      onClick={() => handleRemove(row)}
                                    >
                                      {actioningId === id ? (
                                        <>
                                          <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-1"
                                          />
                                          Disconnecting…
                                        </>
                                      ) : (
                                        <>
                                          <FontAwesomeIcon
                                            icon={faUnlink}
                                            className="me-2"
                                            fixedWidth
                                          />
                                          Disconnect
                                        </>
                                      )}
                                    </Dropdown.Item>
                                  )}
                                </div>
                              </CardDropdown>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </SimpleBarReact>
          )}
          {shareRequests.length === 0 && !loading && (
            <div className="text-center py-5 text-600 fs--1">
              No share requests found.
            </div>
          )}
        </>
      )}
    </>
  );
};

AdminNotariesShareTab.propTypes = {
  direction: PropTypes.oneOf(['SENT', 'RECEIVED']).isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default AdminNotariesShareTab;
